// useTalk — the conversation spine for the desktop Talk surface.
//
// One box does both jobs: a plain dump is captured (POST /messages, capture-ack)
// and a question is recalled/answered (POST /chat/ask). The hook owns the
// in-memory transcript and the optimistic "jotted & safe" state so the UI stays
// presentational.
//
// Routing is intentionally light — we never make the user choose "jot" vs
// "ask". A trailing '?' or an interrogative opening (wh-word + auxiliary)
// routes to recall; everything else is captured. A dump misrouted to recall
// would never be saved (there is no fallback), so the heuristic is
// deliberately conservative: ambiguity defaults to capture.

import { useCallback, useState } from 'react';
import { sendMessage, sendPhoto } from '../api/client';
import type { AssistantReply } from '../api/types';
import { askQuestion, type AskResponse } from '../api/ask';

export type TalkRole = 'you' | 'jotly';

export type TalkTurn = {
  id: string;
  role: TalkRole;
  /** Plain text shown in the bubble. */
  text: string;
  /** True while the capture is still being acknowledged / enriched. */
  pending?: boolean;
  /** Optimistic capture-ack marker — renders the "jotted & safe" affordance. */
  jotted?: boolean;
  /** Recall citations, when this turn is an answer. */
  citations?: AskResponse['citations'];
  /** Routing label the assistant took for this turn. */
  kind?: 'capture' | 'recall';
  /** Backend classification (TASK, DUMP, SHOPPING, etc.) — shown as a correction toggle. */
  messageType?: string | null;
};

let seq = 0;
const nextId = () => `t${Date.now()}_${seq++}`;

/**
 * Heuristic: does this read like a question to recall, or a thing to capture?
 * Capture is the safe default — only an unambiguous interrogative shape routes
 * to recall (a trailing '?', or a wh-word followed by an auxiliary verb).
 * Imperative prefixes ("remind me…", "find…", "do…", "show me…") are dumps,
 * not questions — a misrouted dump is never saved, so ambiguity ALWAYS
 * defaults to capture.
 */
export function looksLikeQuestion(text: string): boolean {
  const t = text.trim().toLowerCase();
  if (t.endsWith('?')) return true;
  // Wh-word contractions: "what's the wifi password" etc.
  if (/^(what|when|where|who|why|how)['’]s\b/.test(t)) return true;
  // Wh-word + auxiliary: "when did i…", "what is…", "where are…".
  return /^(what|when|where|who|why|how)\s+(am|is|are|was|were|do|does|did|have|has|had|can|could|should|would|will)\b/.test(
    t,
  );
}

function captureLine(reply: AssistantReply): string {
  // Prefer the assistant's own confirmation; fall back to a calm default.
  const msg = reply.message?.trim();
  return msg && msg.length > 0 ? msg : 'Jotted & safe.';
}

export function useTalk() {
  const [turns, setTurns] = useState<TalkTurn[]>([]);
  // Sends are CONCURRENT, never serialised — rapid-fire dumps must never be
  // dropped behind an in-flight request. Each send appends its own bubble
  // immediately and resolves/fails independently; `busy` is just a count of
  // requests still in flight (for quiet UI affordances, never gating input).
  const [inflight, setInflight] = useState(0);
  const busy = inflight > 0;

  const begin = useCallback(() => setInflight((n) => n + 1), []);
  const end = useCallback(() => setInflight((n) => Math.max(0, n - 1)), []);

  const append = useCallback((turn: TalkTurn) => {
    setTurns((prev) => [...prev, turn]);
    return turn.id;
  }, []);

  const patch = useCallback((id: string, next: Partial<TalkTurn>) => {
    setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, ...next } : t)));
  }, []);

  /** Send a free-text line — routed to capture or recall. */
  const send = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text) return;

      append({ id: nextId(), role: 'you', text });

      if (looksLikeQuestion(text)) {
        const replyId = append({ id: nextId(), role: 'jotly', text: '', pending: true, kind: 'recall' });
        begin();
        try {
          const res = await askQuestion(text);
          patch(replyId, { text: res.answer, citations: res.citations, pending: false });
        } catch (e) {
          patch(replyId, {
            text: e instanceof Error ? `Couldn't search just now — ${e.message}` : "Couldn't search just now.",
            pending: false,
          });
        } finally {
          end();
        }
        return;
      }

      // Capture path — optimistic ack, then confirm on the server's reply.
      const ackId = append({
        id: nextId(),
        role: 'jotly',
        text: 'Jotted & safe.',
        pending: true,
        jotted: true,
        kind: 'capture',
      });
      begin();
      try {
        const reply = await sendMessage(text);
        patch(ackId, { text: captureLine(reply), pending: false, messageType: reply.messageType });
      } catch (e) {
        patch(ackId, {
          text: e instanceof Error ? `Couldn't save that — ${e.message}` : "Couldn't save that — try again.",
          pending: false,
          jotted: false,
        });
      } finally {
        end();
      }
    },
    [append, patch, begin, end],
  );

  /** Capture an OCR'd photo / screenshot through the same pipeline. */
  const sendImage = useCallback(
    async (imageBase64: string, label = 'Screenshot') => {
      append({ id: nextId(), role: 'you', text: `📎 ${label}` });
      const ackId = append({
        id: nextId(),
        role: 'jotly',
        text: 'Reading the image…',
        pending: true,
        jotted: true,
        kind: 'capture',
      });
      begin();
      try {
        const reply = await sendPhoto(imageBase64);
        patch(ackId, { text: captureLine(reply), pending: false, messageType: reply.messageType });
      } catch (e) {
        patch(ackId, {
          text: e instanceof Error ? `Couldn't read that image — ${e.message}` : "Couldn't read that image.",
          pending: false,
          jotted: false,
        });
      } finally {
        end();
      }
    },
    [append, patch, begin, end],
  );

  /** Post a recall question directly (used by the calendar "see all" peek). */
  const ask = useCallback(
    async (question: string) => {
      append({ id: nextId(), role: 'you', text: question });
      const replyId = append({ id: nextId(), role: 'jotly', text: '', pending: true, kind: 'recall' });
      begin();
      try {
        const res = await askQuestion(question);
        patch(replyId, { text: res.answer, citations: res.citations, pending: false });
      } catch (e) {
        patch(replyId, {
          text: e instanceof Error ? `Couldn't answer just now — ${e.message}` : "Couldn't answer just now.",
          pending: false,
        });
      } finally {
        end();
      }
    },
    [append, patch, begin, end],
  );

  return { turns, busy, send, sendImage, ask };
}
